import type { ReactElement } from 'react';
import { useContext, useMemo, useCallback } from 'react';
import { ExternalLink } from 'react-feather';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatContextType, extractBrandInsightsIds, isBrandInsightsContext } from '@trinity/chat';
import { BrandInsightsModalContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { type To } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { getLocationFor } from '@trinity/chat-components/utils';
import { useAvatarPanel } from '../DigitalTwin/AvatarPanel.context';
import { Link } from '../presentation/Link';

// -- Presentation --

type SourceLinkPresentationProps = {
  to: To;
  onClick?: () => void;
  target?: string;
  showLabel: boolean;
};

function SourceLinkPresentation({ to, onClick, target, showLabel }: SourceLinkPresentationProps): ReactElement {
  return (
    <Link to={to} onClick={onClick} target={target}>
      <TextContainer>
        {showLabel && <Text>Jump to Page </Text>}
        <StyledExternalLink />
      </TextContainer>
    </Link>
  );
}

// -- Controller --

function SourceLinkController(): ReactElement | null {
  const sourceLocation = useSourceLocation();
  const isFullpageChat = useIsFullPageChat();
  const { isDigitalTwin } = useAvatarPanel();
  const [modalIsOpen, toggleModal] = useContext(BrandInsightsModalContext);

  const onClick = useCallback(() => {
    if (!isFullpageChat && modalIsOpen) {
      toggleModal(false);
    }
  }, [isFullpageChat, modalIsOpen, toggleModal]);

  if (!sourceLocation) {
    return null;
  }

  const { to, newTab } = sourceLocation;

  return (
    <SourceLinkPresentation
      to={to}
      onClick={onClick}
      target={newTab ? '_blank' : undefined}
      showLabel={isFullpageChat && !isDigitalTwin}
    />
  );
}

export { SourceLinkController as SourceLink };
export { SourceLinkPresentation };
export type { SourceLinkPresentationProps };

// -- Hook --

type SourceLinkState = {
  to: To;
  newTab?: boolean;
};

const useSourceLocation = (): SourceLinkState | null => {
  const chat = useContext(ActiveChatSessionContext);
  return useMemo(() => resolveSourceLocation(chat.context), [chat.context]);
};

// -- Pure resolvers --

function resolveSourceLocation(context: Chat.InstanceContextWithMetadata): SourceLinkState | null {
  switch (context.type) {
    case ChatContextType.KolProfile:
    case ChatContextType.Transcript:
    case ChatContextType.Global:
      return null;

    case ChatContextType.Document:
      return { to: getLocationFor.workspaces.file({ fileId: context.data.documentId }) };

    case ChatContextType.Project:
      return { to: getLocationFor.project.root({ slug: String(context.data.projectId) }) };

    case ChatContextType.Brand:
      return { to: getLocationFor.brand.root({ brandId: context.data.brandId }) };

    case ChatContextType.Folder:
      return { to: getLocationFor.workspaces.folder({ folderId: context.data.folderId }) };

    case ChatContextType.Agent:
      return resolveAgentSourceLocation(context);

    case ChatContextType.MessageGeneration:
      return resolveSubjectLink(context.data.subject?.link);

    case ChatContextType.Trini:
      return null;

    default:
      throw new UnreachableCaseError(context);
  }
}

function resolveAgentSourceLocation(context: Chat.InstanceContextWithMetadata): SourceLinkState | null {
  if (isBrandInsightsContext(context)) {
    const location = resolveFromBrandInsightsIds(extractBrandInsightsIds(context));
    if (location) return location;
  }
  const link = (context.data as { subject?: { link?: To } }).subject?.link;
  return resolveSubjectLink(link);
}

function resolveSubjectLink(link: To | undefined | null): SourceLinkState | null {
  return link ? { to: link, newTab: true } : null;
}

function resolveFromBrandInsightsIds(
  ids: ReturnType<typeof extractBrandInsightsIds>,
): SourceLinkState | null {
  if (ids.brandId) return { to: getLocationFor.brand.root({ brandId: ids.brandId }) };
  if (ids.projectId) return { to: getLocationFor.project.root({ slug: String(ids.projectId) }) };
  if (ids.folderId) return { to: getLocationFor.workspaces.folder({ folderId: ids.folderId }) };
  if (ids.fileId) return { to: getLocationFor.workspaces.file({ fileId: ids.fileId }) };
  return null;
}

// -- Styles --

const Text = styled.div(({ theme }) => ({
  color: theme.palette.sentiment.primary.main,
  marginRight: 5,
}));

const TextContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
});

const StyledExternalLink = styled(ExternalLink)(({ theme }) => ({
  color: theme.palette.sentiment.primary.main,
}));
